import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react"
import { fetchBalance } from "@wagmi/core"
import { constants } from "@src/constants"

type AccountBalance = {
  ethBalance: bigint
  usdcBalance: bigint
  ethSenderBalance: bigint
  usdcSenderBalance: bigint
  ethSenderBridge: bigint
}

export const getAccountBalance = async (
  account: `0x${string}`,
  sender: `0x${string}`,
  setAccountBalance: Dispatch<SetStateAction<AccountBalance>>,
  bridgeChainId: number,
  chainId: number
) => {
  const currentAccountBalance = await _getAccountBalance(
    account,
    sender,
    bridgeChainId,
    chainId
  )

  setAccountBalance(currentAccountBalance)
}

export const useAccountBalance = (
  account: `0x${string}`,
  sender?: `0x${string}`,
  initAccountBalance?: AccountBalance,
  bridgeChainId?: number,
  chainId?: number
) => {
  const [accountBalance, setAccountBalance] =
    useState<AccountBalance>(initAccountBalance)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const clearAndSetInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
    intervalRef.current = setInterval(() => {
      getAccountBalance(
        account,
        sender,
        setAccountBalance,
        bridgeChainId,
        chainId
      )
    }, 15000)
  }

  const mutate = () => {
    getAccountBalance(
      account,
      sender,
      setAccountBalance,
      bridgeChainId,
      chainId
    )
    clearAndSetInterval()
  }

  useEffect(() => {
    if (
      initAccountBalance &&
      Object.values(initAccountBalance).some((v) => v === undefined)
    ) {
      getAccountBalance(
        account,
        sender,
        setAccountBalance,
        bridgeChainId,
        chainId
      )
    }

    clearAndSetInterval()

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [account, sender])

  return { data: accountBalance, mutate }
}

export const _getAccountBalance = async (
  account: `0x${string}`,
  sender: `0x${string}`,
  bridgeChainId: number,
  chainId: number
) => {
  if (account) {
    const [
      ethBalance,
      usdcBalance,
      ethSenderBalance,
      usdcSenderBalance,
      ethSenderBalanceBridge
    ] = await Promise.all([
      fetchBalance({
        address: account,
        chainId
      }),
      fetchBalance({
        address: account,
        token: constants[chainId].addresses["USDCPriceFeed"],
        chainId
      }),
      sender &&
        sender != account &&
        fetchBalance({
          address: sender,
          chainId
        }),
      sender &&
        sender != account &&
        fetchBalance({
          address: sender,
          token: constants[chainId].addresses["USDCPriceFeed"],
          chainId
        }),
      sender &&
        fetchBalance({
          address: sender,
          chainId: bridgeChainId
        })
    ])

    return {
      ethBalance: ethBalance?.value,
      usdcBalance: usdcBalance?.value,
      ethSenderBalance: ethSenderBalance?.value,
      usdcSenderBalance: usdcSenderBalance?.value,
      ethSenderBridge: ethSenderBalanceBridge?.value
    }
  }
}
